import React, { useEffect, useState } from 'react';
import Apirest from '../../../Content/Apirest';
import { loadAvailableTags } from '../../../Commons/Utils/tags';
import { useContent } from '../../../Content/cms';
import './index.css';

import { useFormFields } from '../../../Commons/Components/FormFields';
import { getEmptyValue, PopUpSuccess } from '../../../Commons/Helpers';

// Import interfaces
import { Participant, Locale } from "../../interfaces";
import { FormSubmitData } from '../../../Commons/Components/FormFields/types';
import { FormSectionScheme, FieldScheme, Option } from '../../../Commons/Components/FormFields/types';

// Import the store
import { ADD_OR_UPDATE_PARTICIPANT } from "../../../store/participants/types";
import { store } from "../../../store";

// Import JSX Components
import { Button, Col, Container, Form, Row, Spinner, Badge, InputGroup, Accordion, Card } from "react-bootstrap";
import { FormField } from "../../../Commons/Components/FormFields/Field";
import { RowSpinner } from "../../../Commons/MultipleComponents";


// Load form schema
import form_schema from './form_schema.json';


const CreatePatient : React.FC = () => {

    let getContent = useContent();
    let today= new Date().toISOString().split("T")[0];
    
    const {
        sections,
        setSections,
        submitHandler,
        onChange,
        state
    } = useFormFields();

    const [assignedTags, setAssignedTags] = useState<string[]>([]);
    const [createTagValue, setCreateTagValue] = useState<string>('');
    const [availableTags, setAvailableTags] = useState<string[]>([]);

    function addTag(tag_name: string) {
        tag_name = tag_name.toLowerCase();
        setAssignedTags(prev => {
            if (prev.find(t => t === tag_name) === undefined) {
                return [...prev, tag_name];
            }
            return prev;
        });
    }

    function createTag(e: any) {
        if (e && e.preventDefault) e.preventDefault();
        let tag_name = (createTagValue || '').toString().trim().toLowerCase();
        if (tag_name !== '') {
            addTag(tag_name);
            // add to availableTags so it can be reused
            setAvailableTags(prev => prev.find(t => t === tag_name) ? prev : [...prev, tag_name]);
            setCreateTagValue('');
        }
    }

    function removeTag(tag_name: string) {
        tag_name = tag_name.toLowerCase();
        setAssignedTags(prev => prev.filter(t => t !== tag_name));
    }

    useEffect(
        () => {
            // Copied, as the schema is filled in below and the imported json is
            // shared by every mount of this form
            const new_sections : FormSectionScheme[] = [{
                name: '',
                description: '',
                fields: JSON.parse(JSON.stringify(form_schema)) as FieldScheme[]
            }];

            // Add form options labels from CMS
            for (let section of new_sections) {
                for (let field of section.fields) {
                    if (typeof field.options !== 'undefined') {
                        field.options = field.options.map((option: Option) => ({
                            ...option,
                            label: getContent(option.label)
                        }));
                    }
                }
            }

            // Ad max date on diagnose date
            for (let section of new_sections) {
                for (let field of section.fields) {
                    if (field.name === "diagnose_date") {
                        field.max = today;
                    } else if (field.name === "birthday"){
                        field.max = today;
                    }
                }
            }
            
            // Add locales to the form
            Apirest.get_locales((response: Locale[]) => {
                for (let section of new_sections) {
                    for (let field of section.fields) {
                        if (field.name === 'locale' && field.options === undefined) {
                            field.options = response.map(locale => ({
                                value: locale.id.toString(),
                                label: locale.code
                            }));           
                        }
                    }
                }

                setSections(new_sections);
            });

            // Load available tags using shared helper
            loadAvailableTags((tags: string[]) => setAvailableTags(tags));
        },
        [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    function onSubmit(data: FormSubmitData) {
        let promise = new Promise<void>(
            (resolve, reject) => {
                const payload = {
                    ...data,
                    tags: assignedTags
                };

                Apirest.create_participant(
                    payload,
                    (participant: Participant) => {
                        store.dispatch({
                            type: ADD_OR_UPDATE_PARTICIPANT,
                            payload: participant
                        });
                        PopUpSuccess(
                            getContent("home__form__server_response__success").replace('{participant_full_name}', participant.code)
                        );
                        resolve();
                    },
                    (jqXHR: JQueryXHR) => {
                        reject(jqXHR.responseJSON);
                    }
                );
            }
        );

        return promise;
    }

    if (sections.length === 0) {
        return <RowSpinner show className='mt-3'/>;
    }

    return (
        <Container className={'sticky-top h-100 mb-0'}>
            <Form autoComplete={'off'} onSubmit={submitHandler(onSubmit)} className={'text-center mb-3 sticky-top first'}>
                <h3 className={'text-primary py-3 my-3'}>{getContent('home__form__form_title')}
                </h3>
                <Form.Group className={'text-left'}>
                    <Container>
                        <Row className={'d-flex justify-content-center completeName'}>
                            {sections[0].fields.map((field,idx) =>
                                <Col key={idx} xs={10}>
                                    <FormField
                                        {...field}
                                        autoComplete={'off'}
                                        serverErrors={state.errors[field.name]}
                                        value={state.values[field.name] || getEmptyValue(field.type)}
                                        onChange={onChange}
                                        useCMS={false /*TODO(Lao): customize error messages in backend and then use CMS*/}
                                    />
                                </Col>
                            )}
                        </Row>

                        <Row className={'my-3 d-flex justify-content-center'}>
                            <Col xs={10} className={'mb-2'}>
                                <Accordion>
                                    <Card>
                                        <Accordion.Toggle as={Card.Header} className='cursor' eventKey="0">
                                            <p className='pb-0 mb-0'>{getContent('add_tag__select_available_tag__title')}</p>
                                        </Accordion.Toggle>
                                        <Accordion.Collapse eventKey="0">
                                            <Card.Body>
                                                {availableTags.map((tag, i) =>
                                                    assignedTags.includes(tag)
                                                        ? null :
                                                        <Badge
                                                            key={i.toString()}
                                                            className={'mr-1 ml-0 cursor badge-tags'}
                                                            onClick={() => addTag(tag)}
                                                        >{tag}</Badge>
                                                )}
                                            </Card.Body>
                                        </Accordion.Collapse>
                                    </Card>
                                </Accordion>
                                <InputGroup className={'mt-2'}>
                                    <Col sm={6} className='m-0 p-0 d-flex justify-content-center'>
                                        <Form.Control
                                            type={'text'}
                                            autoComplete={'off'}
                                            placeholder={getContent('add_tag__create__form__text_placeholder')}
                                            value={createTagValue}
                                            onChange={(e) => setCreateTagValue(e.target.value)}
                                        />
                                    </Col>
                                    <Col className='m-0 d-flex justify-content-center'>
                                        <Button type={'button'} onClick={createTag} className={'create-tag-button pr-0'} size={'sm'}>{getContent('add_tag__create__form__button')}</Button>
                                    </Col>
                                </InputGroup>
                            </Col>
                            <Col xs={10} className={'d-flex flex-wrap'}>
                                {assignedTags.length > 0 ? assignedTags.map((t, i) => (
                                    <div key={i.toString()} className={'d-flex align-items-center mr-3 mt-2'}>
                                        <Badge className={'badge-tags'}>{t}</Badge>
                                        <Button variant={'link'} onClick={() => removeTag(t)} className={'p-0 ml-1'}>x</Button>
                                    </div>
                                )) : <div className={'bg-light p-3 text-center'}>{getContent('admin__form__tags_assigned__empty') || 'No hay tags asignados'}</div>}
                            </Col>
                        </Row>
                        
                        <Row className={'d-flex justify-content-center mt-2'}>
                            <Button className={'m-2 col-9 registerParticipant'} variant={'primary'} type={'submit'} block disabled={state.sending}>
                                {!state.sending && getContent('home__form__register_button')}
                                {state.sending && <Spinner animation={'border'} size={'sm'} color={'#FFFFFF'} />}
                            </Button>
                        </Row>
                    </Container>
                </Form.Group>
            </Form>
        </Container>
    );
};

export default CreatePatient;